/**
 * SavedContentManagement — internal module for CustomContentService.
 *
 * Sprint 4 (#54): extracted from CustomContentService.js as part of the
 * decomposition that keeps each module under the 800-LOC limit
 * (CLAUDE.md Rule 3). Houses the bookmark-style "saved content" tracking
 * logic that was merged in from SavedContentService in Sprint 3 (#51).
 *
 * NOTE — Internal module: import from CustomContentService instead. The
 * `_customContent/` prefix signals "do not import directly". The facade
 * keeps thin instance-method wrappers so existing call sites
 * (useContentArchive, etc.) continue to work without changes.
 *
 * Implementation pattern: every function takes a `state` object that owns
 * the per-instance cache (`_savedCache`, `_savedCacheTimestamp`,
 * `_savedCacheDuration`). The facade passes `this` as state, which lets
 * tests continue to inspect `service._savedCache` directly.
 */

/**
 * Get all saved content IDs for the current user.
 * Reads through the per-instance cache; populates it on first call.
 */
export async function getSavedContentIds(state, client, instanceLogger) {
  try {
    if (state._savedCache && state._savedCacheTimestamp &&
        (Date.now() - state._savedCacheTimestamp) < state._savedCacheDuration) {
      return state._savedCache;
    }

    if (!client || !client.models || !client.models.SavedContentItem) {
      instanceLogger.warn('GraphQL client or SavedContentItem model not available, returning empty array');
      return [];
    }

    const { data, errors } = await client.models.SavedContentItem.list();

    if (errors) {
      instanceLogger.error('GraphQL errors fetching saved content:', errors);
      return [];
    }

    const contentIds = (data || []).map(item => item.contentId);
    state._savedCache = contentIds;
    state._savedCacheTimestamp = Date.now();
    return contentIds;
  } catch (error) {
    instanceLogger.error('Error fetching saved content:', error);
    return [];
  }
}

/**
 * Save a content item (idempotent — already-saved items return true without
 * a second create).
 */
export async function saveContent(state, client, instanceLogger, contentId) {
  try {
    if (!client || !client.models || !client.models.SavedContentItem) {
      instanceLogger.warn('GraphQL client not available, cannot save content');
      return false;
    }

    const savedIds = await getSavedContentIds(state, client, instanceLogger);
    if (savedIds.includes(contentId)) {
      instanceLogger.info(`Content ${contentId} already saved`);
      return true;
    }

    const { data, errors } = await client.models.SavedContentItem.create({
      contentId,
      savedAt: new Date().toISOString()
    });

    if (errors) {
      instanceLogger.error('GraphQL errors saving content:', errors);
      return false;
    }

    state._savedCache = null;
    state._savedCacheTimestamp = null;
    return true;
  } catch (error) {
    instanceLogger.error('Error saving content:', error);
    return false;
  }
}

/**
 * Remove a saved content item.
 */
export async function unsaveContent(state, client, instanceLogger, contentId) {
  try {
    if (!client || !client.models || !client.models.SavedContentItem) {
      instanceLogger.warn('GraphQL client not available, cannot unsave content');
      return false;
    }

    const { data: savedItems } = await client.models.SavedContentItem.list({
      filter: { contentId: { eq: contentId } }
    });

    if (!savedItems || savedItems.length === 0) {
      instanceLogger.info(`Content ${contentId} not found in saved items`);
      return true;
    }

    for (const item of savedItems) {
      await client.models.SavedContentItem.delete({ id: item.id });
    }

    state._savedCache = null;
    state._savedCacheTimestamp = null;
    return true;
  } catch (error) {
    instanceLogger.error('Error unsaving content:', error);
    return false;
  }
}

/**
 * Toggle saved status of content item. Returns the resulting saved state
 * (true = now saved, false = now unsaved).
 */
export async function toggleSavedContent(state, client, instanceLogger, contentId) {
  try {
    const savedIds = await getSavedContentIds(state, client, instanceLogger);
    const isSaved = savedIds.includes(contentId);

    if (isSaved) {
      await unsaveContent(state, client, instanceLogger, contentId);
      return false;
    } else {
      await saveContent(state, client, instanceLogger, contentId);
      return true;
    }
  } catch (error) {
    instanceLogger.error('Error toggling saved content:', error);
    return false;
  }
}

/**
 * Save multiple content items (bulk operation).
 */
export async function bulkSaveContent(state, client, instanceLogger, contentIds) {
  let success = 0;
  let failed = 0;
  for (const contentId of contentIds) {
    const result = await saveContent(state, client, instanceLogger, contentId);
    if (result) success++; else failed++;
  }
  return { success, failed };
}

/**
 * Remove multiple saved content items (bulk operation).
 */
export async function bulkUnsaveContent(state, client, instanceLogger, contentIds) {
  let success = 0;
  let failed = 0;
  for (const contentId of contentIds) {
    const result = await unsaveContent(state, client, instanceLogger, contentId);
    if (result) success++; else failed++;
  }
  return { success, failed };
}

/**
 * Check if a single content id is saved.
 */
export async function isContentSaved(state, client, instanceLogger, contentId) {
  const savedIds = await getSavedContentIds(state, client, instanceLogger);
  return savedIds.includes(contentId);
}

/**
 * Get the count of saved items.
 */
export async function getSavedCount(state, client, instanceLogger) {
  const savedIds = await getSavedContentIds(state, client, instanceLogger);
  return savedIds.length;
}

/**
 * Clear all saved content for the current user.
 */
export async function clearAllSaved(state, client, instanceLogger) {
  try {
    const { data: savedItems } = await client.models.SavedContentItem.list();
    for (const item of savedItems || []) {
      await client.models.SavedContentItem.delete({ id: item.id });
    }
    state._savedCache = null;
    state._savedCacheTimestamp = null;
    return true;
  } catch (error) {
    instanceLogger.error('Error clearing saved content:', error);
    return false;
  }
}

/**
 * Migrate saved content from localStorage to backend.
 */
export async function migrateFromLocalStorage(state, client, instanceLogger, localStorageIds) {
  if (!localStorageIds || localStorageIds.length === 0) return { migrated: 0, failed: 0 };

  instanceLogger.info(`Migrating ${localStorageIds.length} saved content items from localStorage...`);
  const results = await bulkSaveContent(state, client, instanceLogger, localStorageIds);
  instanceLogger.info(`Migration complete: ${results.success} migrated, ${results.failed} failed`);
  return { migrated: results.success, failed: results.failed };
}

/**
 * Invalidate the saved-content cache (force refresh on next read).
 * Pure state mutation — no I/O.
 */
export function invalidateSavedCache(state) {
  state._savedCache = null;
  state._savedCacheTimestamp = null;
}
